export const CLIENT_CONTEXT_MODES = ['kiosk', 'web', 'mobile', 'display'] as const;

export type ClientContextMode = (typeof CLIENT_CONTEXT_MODES)[number];

export type ClientContextDecision = {
  mode: ClientContextMode;
  reason:
    | 'explicit-mode'
    | 'qr-context'
    | 'display-flag'
    | 'kiosk-flag'
    | 'large-touch'
    | 'default-web';
};

export type ClientContextDeviceSignals = {
  width?: number;
  height?: number;
  coarsePointer?: boolean;
  maxTouchPoints?: number;
};

const VALID_MODES = new Set<string>(CLIENT_CONTEXT_MODES);

function normalizeMode(value: string | null): ClientContextMode | null {
  const normalized = String(value || '').trim().toLowerCase();
  return VALID_MODES.has(normalized) ? (normalized as ClientContextMode) : null;
}

function isTruthyFlag(value: string | null): boolean {
  return ['1', 'true', 'yes', 'y', 'on'].includes(String(value || '').trim().toLowerCase());
}

export function isClientContextMode(value: string): value is ClientContextMode {
  return VALID_MODES.has(String(value || '').trim().toLowerCase());
}

export function detectLargeTouchDevice(signals: ClientContextDeviceSignals = {}): boolean {
  const width = Number(signals.width || 0);
  const height = Number(signals.height || 0);
  const touchCapable = Boolean(signals.coarsePointer) || Number(signals.maxTouchPoints || 0) > 0;
  return touchCapable && width >= 900 && height >= 650;
}

export function resolveClientContext(
  source: URL | URLSearchParams | string,
  deviceSignals: ClientContextDeviceSignals = {},
): ClientContextDecision {
  const url =
    typeof source === 'string'
      ? new URL(source, 'http://enfa.local')
      : source instanceof URLSearchParams
        ? new URL(`http://enfa.local/client?${source.toString()}`)
        : source;
  const params = url.searchParams;

  const explicitMode = normalizeMode(params.get('mode'));
  if (explicitMode) {
    return { mode: explicitMode, reason: 'explicit-mode' };
  }

  if (params.get('table_id') || params.get('qr_token') || params.get('session_id')) {
    return { mode: 'mobile', reason: 'qr-context' };
  }

  if (isTruthyFlag(params.get('display'))) {
    return { mode: 'display', reason: 'display-flag' };
  }

  if (isTruthyFlag(params.get('kiosk'))) {
    return { mode: 'kiosk', reason: 'kiosk-flag' };
  }

  if (detectLargeTouchDevice(deviceSignals)) {
    return { mode: 'kiosk', reason: 'large-touch' };
  }

  return { mode: 'web', reason: 'default-web' };
}

export function resolveClientModeFromPath(pathname: string): ClientContextMode | null {
  const match = String(pathname || '').match(/^\/client\/(kiosk|web|mobile|display)\/?$/i);
  return match ? (match[1].toLowerCase() as ClientContextMode) : null;
}

export function buildClientModeUrl(mode: ClientContextMode, source: URL | string): string {
  const url = typeof source === 'string' ? new URL(source, window.location.origin) : new URL(source);
  url.pathname = `/client/${mode}`;
  url.searchParams.delete('mode');
  return `${url.pathname}${url.search}${url.hash}`;
}

export function readBrowserDeviceSignals(): ClientContextDeviceSignals {
  const coarsePointer = Boolean(
    window.matchMedia && window.matchMedia('(pointer: coarse)').matches,
  );
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    coarsePointer,
    maxTouchPoints: navigator.maxTouchPoints || 0,
  };
}
